import { DEFAULT_PAGE_LIMIT } from 'lib/hooks/usePagedData';
import logger from 'lib/logger';
import React from 'react';
import { isLocations, Location } from './models/LocationModel';
import { locationApi } from './LocationApi';

const LOCATION_STATES = 'ACTIVE,PENDING';

export function useLocationsData(title?: string) {
    const [items, setItems] = React.useState<Location[]>([]);
    // Stack of startKeys for each visited page; the first page has no startKey (undefined).
    const [keyStack, setKeyStack] = React.useState<(string | undefined)[]>([undefined]);
    const [nextKey, setNextKey] = React.useState<string | undefined>();
    const [loading, setLoading] = React.useState(true);

    const startKey = keyStack[keyStack.length - 1];

    // Reset to the first page whenever the search term changes
    React.useEffect(() => {
        setKeyStack([undefined]);
    }, [title]);

    React.useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const { body, ok } = await locationApi.getLocationsPage({
                    state: LOCATION_STATES,
                    limit: DEFAULT_PAGE_LIMIT,
                    startKey,
                    title
                });
                if (cancelled) {
                    return;
                }
                if (!ok) {
                    throw new Error(body.message);
                }
                const { items: locations, page } = body;
                if (isLocations(locations)) {
                    setItems(locations);
                    setNextKey(page?.nextKey);
                } else {
                    logger.error('TypeError: expected Location[], got:', locations);
                    setItems([]);
                    setNextKey(undefined);
                }
            } catch (e) {
                if (!cancelled) {
                    logger.error(e);
                    setItems([]);
                    setNextKey(undefined);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [startKey, title]);

    const hasPreviousPage = keyStack.length > 1;
    const hasNextPage = !!nextKey;

    const onNextPage = React.useCallback(() => {
        if (nextKey) {
            setKeyStack(stack => [...stack, nextKey]);
        }
    }, [nextKey]);

    const onPreviousPage = React.useCallback(() => {
        setKeyStack(stack => (stack.length > 1 ? stack.slice(0, -1) : stack));
    }, []);

    return { items, loading, hasPreviousPage, hasNextPage, onNextPage, onPreviousPage };
}
